import { SealCheckIcon, type IconProps } from '@phosphor-icons/react';

import type { StatutoryDocumentRow } from 'src/features/groups/api';
import type { StatutoryDocumentCounts } from 'src/features/groups/components/statutoryChartSpecs';
import { formatDateBritish } from 'src/lib/utils/formatDate';

type StatutoryDocumentIconProps = Pick<IconProps, 'size' | 'weight' | 'className'>;

export function StatutoryDocumentIcon({ size = 18, weight = 'light', className }: StatutoryDocumentIconProps) {
  return <SealCheckIcon size={size} weight={weight} className={className} aria-hidden />;
}

export function hasStatutoryFileOrNoteEvidence(document: {
  linked_documents?: StatutoryDocumentRow['linked_documents'];
  other_evidence?: StatutoryDocumentRow['other_evidence'];
  training_evidence?: StatutoryDocumentRow['training_evidence'];
}): boolean {
  return (
    (document.linked_documents?.length ?? 0) > 0 ||
    (document.other_evidence?.length ?? 0) > 0 ||
    (document.training_evidence?.length ?? 0) > 0
  );
}

export function statutoryDocumentLabelsByDocumentId(
  documents: readonly StatutoryDocumentRow[]
): ReadonlyMap<string, readonly string[]> {
  const map = new Map<string, string[]>();
  for (const document of documents) {
    for (const linked of document.linked_documents) {
      const names = map.get(linked.id) ?? [];
      if (!names.includes(document.name)) {
        names.push(document.name);
      }
      map.set(linked.id, names);
    }
  }
  return map;
}

export type StatutoryDestructiveAction = 'delete' | 'unlink' | 'move';

export function statutoryDestructiveActionCopy(
  action: StatutoryDestructiveAction,
  fileName: string,
  statutoryNames: readonly string[]
): { title: string; message: string; confirmText: string } {
  const requirementList =
    statutoryNames.length === 1 ? `"${statutoryNames[0]}"` : statutoryNames.map((name) => `"${name}"`).join(', ');

  switch (action) {
    case 'delete':
      return {
        title: 'Delete statutory evidence?',
        message: `"${fileName}" is linked as evidence for ${requirementList}. Deleting it will remove the file from this group and may affect statutory compliance.`,
        confirmText: 'Delete anyway'
      };
    case 'unlink':
      return {
        title: 'Unlink statutory evidence?',
        message: `"${fileName}" is linked as evidence for ${requirementList}. Unlinking it from this group will remove that evidence and may affect statutory compliance.`,
        confirmText: 'Unlink anyway'
      };
    case 'move':
      return {
        title: 'Move statutory evidence?',
        message: `"${fileName}" is linked as evidence for ${requirementList}. Moving it out of this group may affect statutory compliance.`,
        confirmText: 'Continue'
      };
  }
}

export type StatutoryComplianceSummaryCounts = StatutoryDocumentCounts & {
  total: number;
  completePercent: number;
};

export type StatutoryDocumentExpiryFeedItem = {
  kind: 'statutory_document_expired' | 'statutory_document_expiring';
  title: string;
  subtitleParts: string[];
  timestamp: null;
  relevant_to_user: true;
  target_ids: string[];
};

export function formatStatutoryExpiryDate(value: string | null): string {
  return value ? formatDateBritish(value) : '—';
}

export type StatutoryExpiryDisplay = {
  kind: 'date' | 'no_expiry' | 'not_set';
  label: string;
};

/** Requirement has no renewal cycle vs we expected a date but none is known. */
export function statutoryExpiryDisplay(document: StatutoryDocumentRow): StatutoryExpiryDisplay {
  if (document.expiry_date) {
    return { kind: 'date', label: formatStatutoryExpiryDate(document.expiry_date) };
  }
  if ((document.training_evidence?.length ?? 0) > 0 || document.renewal_frequency_months == null) {
    return { kind: 'no_expiry', label: 'No expiry' };
  }
  return { kind: 'not_set', label: 'Not set' };
}

export function daysUntilStatutoryExpiry(value: string | null): number | null {
  if (!value) return null;
  const [year, month, day] = value.split('T')[0].split('-').map(Number);
  if (!year || !month || !day) return null;
  const expiry = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);
  return Math.round((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function statutoryExpiryTitle(name: string, daysToExpiry: number): string {
  if (daysToExpiry < 0) {
    const days = Math.abs(daysToExpiry);
    return `${name} expired ${days} ${days === 1 ? 'day' : 'days'} ago`;
  }
  if (daysToExpiry === 0) {
    return `${name} expires today`;
  }
  return `${name} expires in ${daysToExpiry} ${daysToExpiry === 1 ? 'day' : 'days'}`;
}

export function statutoryDocumentExpiryFeedItems(
  documents: Array<
    Pick<StatutoryDocumentRow, 'required' | 'expiry_date' | 'expiry_status' | 'name' | 'recommendation_id'> & {
      evidenced?: boolean;
      dismissed?: boolean;
      dismissal?: StatutoryDocumentRow['dismissal'];
      linked_documents?: StatutoryDocumentRow['linked_documents'];
      other_evidence?: StatutoryDocumentRow['other_evidence'];
      training_evidence?: StatutoryDocumentRow['training_evidence'];
    }
  >
): StatutoryDocumentExpiryFeedItem[] {
  const ranked: { daysToExpiry: number; item: StatutoryDocumentExpiryFeedItem }[] = [];

  for (const document of documents) {
    const dismissed = document.dismissed === true || document.dismissal != null;
    const evidenced =
      'evidenced' in document && typeof (document as { evidenced?: boolean }).evidenced === 'boolean'
        ? (document as { evidenced: boolean }).evidenced
        : hasStatutoryFileOrNoteEvidence(document);
    const expiryStatus = document.expiry_status;
    if (
      dismissed ||
      !document.required ||
      !evidenced ||
      !document.expiry_date ||
      expiryStatus === 'valid' ||
      expiryStatus === 'pending'
    )
      continue;

    const daysToExpiry = daysUntilStatutoryExpiry(document.expiry_date);
    if (daysToExpiry === null) continue;

    ranked.push({
      daysToExpiry,
      item: {
        kind: expiryStatus === 'expired' ? 'statutory_document_expired' : 'statutory_document_expiring',
        title: statutoryExpiryTitle(document.name, daysToExpiry),
        subtitleParts: ['Required statutory document', `Expires ${formatStatutoryExpiryDate(document.expiry_date)}`],
        timestamp: null,
        relevant_to_user: true,
        target_ids: [document.recommendation_id]
      }
    });
  }

  ranked.sort((a, b) => a.daysToExpiry - b.daysToExpiry);
  return ranked.map((row) => row.item);
}

export function computeStatutoryComplianceSummary(documents: StatutoryDocumentRow[]): StatutoryComplianceSummaryCounts {
  const required = documents.filter((document) => document.required && document.dismissal == null);
  let present = 0;
  let expiring = 0;
  let pending = 0;
  let expired = 0;
  let missing = 0;

  for (const document of required) {
    if (!hasStatutoryFileOrNoteEvidence(document)) {
      missing += 1;
    } else if (document.expiry_status === 'expired') {
      expired += 1;
    } else if (document.expiry_status === 'pending') {
      pending += 1;
    } else if (document.expiry_status === 'expiring') {
      expiring += 1;
    } else {
      present += 1;
    }
  }

  const total = required.length;
  const currentlyPresent = present + expiring;
  const completePercent = total === 0 ? 0 : Math.round((currentlyPresent / total) * 100);

  return { present, expiring, pending, expired, missing, total, completePercent };
}
